/** @format */

import React, { useEffect, useState } from 'react';
import { makeStyles, Table, TableBody, TableCell, TableFooter, TableHead, TableRow } from '@material-ui/core';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { configDefault } from '../../core/config';
import { useDispatch, useSelector } from 'react-redux';
import Filter from '../../interfaces/forms/Filter';
import Order from '../../interfaces/forms/Order';
import { FilterListReducerFieldName } from '../../modules/filter/FilterListReducers';
import { FilterReducerFieldName } from '../../modules/filter/FilterReducers';
import adescomStyle from '../../assets/jss/adescom/adescomStyle';
import Pager from '../../components/Adescom/Pager';
import { history as HistoryCore } from '../../core/history';
import { serviceCodeConstants, serviceCodeReducerType } from '../../modules/servicecode/reducers';
import GridItem from '../../components/Grid/GridItem';
import GridContainer from '../../components/Grid/GridContainer';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import { useHistory, useParams } from 'react-router-dom';
import { ServiceCodeHistoryHelper } from '../../helpers/ServiceCodeHelper';
import { DateHelperTs } from '../../helpers/DateHelperTs';
import TableHeaderContentCell from '../../components/Table/TableHeaderContentCell';
import { ServiceCodeHistoryCollectionInterface } from '../../interfaces/collections/ServiceCodeHistoryCollectionInterface';
import ServiceCodeHistoryInterface from '../../interfaces/platform/ServiceCodeHistory';
import oplAppStyle from '../../assets/themes/OPL/jss/App/OplAppStyle';
import { withStyles } from '@material-ui/core/styles';

interface LocationStateInterface {
    serviceCodeName: string;
    serviceCodeDescription: string;
    history: boolean;
}

const ServiceCodeHistory = (props: { classes: any }): React.ReactElement => {
    const dispatch = useDispatch();

    const { id } = useParams<{ id: string }>();
    const { location } = useHistory<LocationStateInterface>();

    const tableStickyHeader = { root: props.classes.tableStickyHeader };

    const { serviceCodeName, serviceCodeDescription, history } = location.state;

    const { collections } = useSelector((state: { serviceCodeReducers: serviceCodeReducerType }) => {
        return state.serviceCodeReducers;
    });

    const [filters, setFilters] = useState<Array<Filter>>([]);
    const [orders, setOrders] = useState<Array<Order>>([]);

    const updateOrderList = (orders: Array<Order>): void => {
        setOrders(orders);
    };
    const updateFilterList = (filters: Array<Filter>): void => {
        setFilters(filters);
    };

    const clearFilter = (): void => {
        setFilters([{ name: 'serviceCodeId', value: id }]);
        setOrders([{ name: 'modifiedAt', value: 'desc' }]);
        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
    };

    const paginationCallbackFunction = (uri: string | null = null): void => {
        if (!uri || !Object.keys(uri).length) {
            uri = FormGeneralHelper.getPath('/service_code_histories', configDefault.PLATFORM_API_PREFIX);
        }
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(uri),
            serviceCodeConstants.SERVICE_CODE_LIST,
            filters,
            orders,
            (data: ServiceCodeHistoryCollectionInterface) => {
                dispatch({
                    type: serviceCodeConstants.SERVICE_CODE_LIST,
                    collections: data,
                });
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    };

    const goToServiceCode = (param: { id: number; history: boolean; serviceCodeName: string; serviceCodeDescription: string }): void => {
        const redirectUri = FormGeneralHelper.getRouteUrl('/service-code-edit/' + param.id);

        HistoryCore.push(redirectUri, {
            history: param.history,
            serviceCodeName: param.serviceCodeName,
            serviceCodeDescription: param.serviceCodeDescription,
        });
    };

    useEffect(() => {
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/service_code_histories', configDefault.PLATFORM_API_PREFIX)),
            serviceCodeConstants.SERVICE_CODE_HISTORY_LIST,
            [{ name: 'serviceCodeId', value: id }],
            [{ name: 'modifiedAt', value: 'desc' }],
            (data: ServiceCodeHistoryCollectionInterface) => {
                dispatch({
                    type: serviceCodeConstants.SERVICE_CODE_HISTORY_LIST,
                    collections: data,
                });
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    }, [dispatch, id]);

    return (
        <GridContainer>
            <GridItem xs={12}>
                <CustomTextButtom
                    size={'sm'}
                    label={'service_code:b.go_to_service_code'}
                    callback={goToServiceCode}
                    callbackParam={{
                        id: id,
                        history: false,
                        serviceCodeName: serviceCodeName,
                        serviceCodeDescription: serviceCodeDescription,
                    }}
                />
                <Table>
                    <TableHead>
                        <TableRow>
                            {(filters.length > 0 || orders.length > 0) && (
                                <>
                                    <CustomTextButtom size={'sm'} label={'general:common.search'} callback={paginationCallbackFunction} />
                                    <CustomTextButtom size={'sm'} label={'general:common.clear_filter'} callback={clearFilter} />
                                </>
                            )}
                        </TableRow>
                        <TableRow>
                            <TableCell classes={tableStickyHeader} align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="service_code:h.operation_type"
                                    fieldName="entity"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="select"
                                    selectComponent="headerCheckBoxSimpleList"
                                    elementList={ServiceCodeHistoryHelper.getOperationTypesList()}
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={paginationCallbackFunction}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} align={'left'}>
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="service_code:h.details"
                                    fieldName="details"
                                    showSort={false}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="string"
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={paginationCallbackFunction}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader} padding="none" align="left">
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="service_code:h.modified_at"
                                    fieldName="modifiedAt"
                                    showSort={true}
                                    onlyFilter={false}
                                    defaultDirection="desc"
                                    searchFieldType="date"
                                    dateFormat="RRRR-MM-DD"
                                    beforeSetToMax={true}
                                    afterSetToMin={true}
                                    dateFromTo={true}
                                    breakLine={true}
                                    timeFormat={false}
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={paginationCallbackFunction}
                                />
                            </TableCell>
                            <TableCell classes={tableStickyHeader}>
                                <TableHeaderContentCell
                                    textAlign="left"
                                    title="service_code:h.modified_by"
                                    fieldName="platformUser"
                                    showSort={true}
                                    onlyFilter={true}
                                    defaultDirection="desc"
                                    searchFieldType="string"
                                    filters={filters}
                                    sort={orders}
                                    updateOrderList={updateOrderList}
                                    updateFilterList={updateFilterList}
                                    searchActionCallback={paginationCallbackFunction}
                                />
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {collections &&
                            collections['hydra:member'].map((el: ServiceCodeHistoryInterface) => {
                                return (
                                    <TableRow key={el.id}>
                                        <TableCell classes={tableStickyHeader}>{ServiceCodeHistoryHelper.getOperationType(el.entity)}</TableCell>
                                        <TableCell classes={tableStickyHeader}>
                                            {ServiceCodeHistoryHelper.getDetails(el.entity, el.crudType, el.field, el.lastValue, el.newValue)}
                                        </TableCell>
                                        <TableCell classes={tableStickyHeader}>{DateHelperTs.format(el.modifiedAt, 'YYYY-MM-DD HH:mm:ss')}</TableCell>
                                        <TableCell classes={tableStickyHeader}>{el.platformUser}</TableCell>
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={4}>
                                {collections && collections['hydra:view'] && (
                                    <Pager callback={paginationCallbackFunction} views={collections['hydra:view']} />
                                )}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </GridItem>
        </GridContainer>
    );
};

export default withStyles(oplAppStyle)(ServiceCodeHistory);
